import { useState } from "react";
import { Button } from "../ui/Button";
import { Chevron } from "../ui/Chevron";
import { useStartSpreken } from "../../lib/exam-start";
import { useT } from "../../lib/use-t";

type DrillMode = "deel-1" | "deel-2" | "deel-3" | "deel-4";

const DRILLS: { id: DrillMode; labelKey: string; subKey: string }[] = [
  { id: "deel-1", labelKey: "vandaag.dagtaak.deel1Label", subKey: "vandaag.dagtaak.deel1Sub" },
  { id: "deel-2", labelKey: "vandaag.dagtaak.deel2Label", subKey: "vandaag.dagtaak.deel2Sub" },
  { id: "deel-3", labelKey: "vandaag.dagtaak.deel3Label", subKey: "vandaag.dagtaak.deel3Sub" },
  { id: "deel-4", labelKey: "vandaag.dagtaak.deel4Label", subKey: "vandaag.dagtaak.deel4Sub" },
];

/**
 * Hero "Dagtaak" card. The warm CTA starts the full speaking oefenexamen;
 * the disclosure exposes a 2×2 grid that drills a single Deel. Both routes
 * go through the shared `useStartSpreken` hook (DRY with Oefenen / ModePicker)
 * which POSTs /exam/start and navigates to the runner.
 *
 * Streak pill from the mockup is OMITTED: no streak data source exists, and
 * _DECISIONS.md forbids fabricated metrics.
 */
export function DagtaakHero() {
  const t = useT();
  const { start, pending, error } = useStartSpreken(t("vandaag.dagtaak.startError"));
  const [drillsOpen, setDrillsOpen] = useState(false);

  function run(mode: "full" | DrillMode) {
    void start(mode);
  }

  return (
    <section className="relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-6 shadow-[var(--shadow)] sm:p-[30px]">
      {/* Decorative warm blob, top-right. Purely cosmetic. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-14 -top-14 h-44 w-44 rounded-full bg-[var(--warm-soft)] sm:-right-[60px] sm:-top-[60px] sm:h-[200px] sm:w-[200px]"
      />
      <div className="relative">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("vandaag.dagtaak.eyebrow")}
        </p>
        <p className="mt-2 font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("vandaag.dagtaak.meta")}
        </p>
        <h2 className="mt-3.5 text-2xl font-semibold tracking-tight sm:text-[27px]">
          {t("vandaag.dagtaak.title")}
        </h2>
        <p className="mb-6 mt-3 max-w-[42ch] font-serif-italic text-base leading-relaxed text-[var(--ink-muted)] sm:text-lg">
          {t("vandaag.dagtaak.description")}
        </p>

        <Button
          variant="warm"
          size="lg"
          onClick={() => run("full")}
          disabled={pending}
          className="w-full sm:w-auto"
        >
          {pending ? t("vandaag.dagtaak.preparing") : t("vandaag.dagtaak.startCta")}
          {!pending ? <ArrowGlyph /> : null}
        </Button>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-[var(--fail)]">
            {error}
          </p>
        ) : null}

        <details
          className="mt-[22px] border-t border-[var(--border)] pt-[18px]"
          open={drillsOpen}
        >
          <summary
            onClick={(e) => {
              e.preventDefault();
              setDrillsOpen((o) => !o);
            }}
            className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-[var(--ink-muted)] [&::-webkit-details-marker]:hidden"
          >
            <Chevron open={drillsOpen} />
            <span>{t("vandaag.dagtaak.drillToggle")}</span>
            <span className="ml-1 font-mono-badge text-xs text-[var(--ink-faint)]">
              {t("vandaag.dagtaak.drillToggleDuration")}
            </span>
          </summary>
          <div className="mt-3.5 grid grid-cols-2 gap-2.5">
            {DRILLS.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => run(d.id)}
                disabled={pending}
                className="flex items-baseline justify-between gap-2.5 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3.5 py-3 text-left transition-[transform,background-color,border-color] duration-150 ease-out hover:-translate-y-px hover:border-[var(--accent)] hover:bg-[var(--accent-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="text-sm font-semibold text-[var(--ink)]">{t(d.labelKey)}</span>
                <span className="text-xs text-[var(--ink-muted)]">{t(d.subKey)}</span>
              </button>
            ))}
          </div>
        </details>
      </div>
    </section>
  );
}

function ArrowGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
